import { createModel } from '@rematch/core'
import { format } from 'date-fns'
import { FETCHING, IDLE } from '../helpers/constants'
import { getItemsCountFrom } from '../helpers/getItemsCount'
import { groupByStatus } from '../helpers/groupOrders'
import { fetchFrom } from '../utils/fetchData'
import { emptyGuid } from '../constants'
import { getServiceData, sendServiceData } from '../utils/httpClient'
import type { RootModel } from '.'
import { Status } from '../types/Status'
import { CateringOrder, Order, OrderHistory, OrderItem, OrderStatusUpdate, OrderUpdateOperation, UpdatedOrder } from '../types/Order'
import logError from '../utils/logging'
import groupCateringOrderItems from '../helpers/groupCateringOrderItems'
import { getOrderHistory } from '../helpers/getOrderHistory'
import { Product } from '../types/Product'
import { CreateOrderPayload } from '../types/CreateOrderPayload'
import { PaymentMethod } from '../types/PaymentMethod'
import { DeliveryProvider } from '../types/DeliveryProviders'

interface OrderState {
  status: Status,
  allOrders: Order[],
  ordersWaiting: Order[],
  ordersBeingPrepared: Order[],
  ordersReadyForDelivery: Order[],
  ordersPickedUp: Order[],
  orderHistory: OrderHistory[],
  cateringOrders: CateringOrder[],
  cateringHistory: CateringOrder[],
  cateringReport: CateringOrder[],
}

export const order = createModel<RootModel>()({
  state: {
    status: IDLE,
    allOrders: [],
    ordersWaiting: [],
    ordersBeingPrepared: [],
    ordersReadyForDelivery: [],
    ordersPickedUp: [],
    orderHistory: [],
    cateringOrders: [],
    cateringHistory: [],
    cateringReport: [],
  } as OrderState,
  reducers: {
    startFetching (state) {
      return { ...state, status: FETCHING }
    },
    setOrdersWaiting (state, payload: Order[]) {
      return { ...state, status: IDLE, ordersWaiting: payload }
    },
    setAllOrders (state, payload: Order[]) {
      return { ...state, status: IDLE, allOrders: payload }
    },
    setOrdersBeingPrepared (state, payload: Order[]) {
      return { ...state, status: IDLE, ordersBeingPrepared: payload }
    },
    setOrdersReadyForDelivery (state, payload: Order[]) {
      return { ...state, status: IDLE, ordersReadyForDelivery: payload }
    },
    setOrdersPickedUp (state, payload: Order[]) {
      return { ...state, status: IDLE, ordersPickedUp: payload }
    },
    setOrderHistory (state, payload: OrderHistory[]) {
      return { ...state, status: IDLE, orderHistory: payload }
    },
    setCateringOrders (state, payload: CateringOrder[]) {
      return { ...state, status: IDLE, cateringOrders: payload }
    },
    setCateringHistory (state, payload: CateringOrder[]) {
      return { ...state, status: IDLE, cateringHistory: payload }
    },
    setCateringReport (state, payload: CateringOrder[]) {
      return { ...state, status: IDLE, cateringReport: payload }
    },
  },
  effects: (dispatch) => ({
    clearOrders () {
      dispatch.order.setAllOrders([])
      dispatch.order.setOrdersWaiting([])
      dispatch.order.setOrdersBeingPrepared([])
      dispatch.order.setOrdersReadyForDelivery([])
      dispatch.order.setOrdersPickedUp([])
    },
    async fetchOrders (payload) {
      const { storeId, fetching } = payload
      if (fetching === true) {
        dispatch.order.startFetching()
      }

      try {
        const storeOrders = await fetchFrom<Order[]>(`/api/stores/${storeId}/orders`)
        if (storeOrders && storeOrders.length > 0) {
          const storeOrdersWithItemCount = getItemsCountFrom(storeOrders)
          dispatch.order.setAllOrders(storeOrdersWithItemCount)
          const { waiting = [], beingPrepared = [], readyForDelivery = [], pickedUp = [] } = groupByStatus<Order>(storeOrdersWithItemCount)

          dispatch.order.setOrdersWaiting(waiting)
          dispatch.order.setOrdersBeingPrepared(beingPrepared)
          dispatch.order.setOrdersReadyForDelivery(readyForDelivery)
          dispatch.order.setOrdersPickedUp(pickedUp)
        } else {
          dispatch.order.clearOrders()
        }
      } catch (error) {
        logError(error as Error)
        dispatch.order.clearOrders()
      }
    },
    async fetchOrderById (payload, state) {
      const { storeId, orderId, loading } = payload

      if (loading === true) {
        dispatch.order.startFetching()
      }

      try {
        const newOrder = await fetchFrom<Order>(`/api/stores/${storeId}/orders/${orderId}`)
        if (newOrder) {
          const newOrderStatus = newOrder?.status || 0
          const countItemsOnNewOrder = getItemsCountFrom([newOrder])
          if (newOrderStatus >= 0 || newOrderStatus < 4) {
            if (state.order.allOrders) {
              dispatch.order.setAllOrders([...state.order.allOrders, ...countItemsOnNewOrder])
            } else {
              dispatch.order.setAllOrders([...countItemsOnNewOrder])
            }

            switch (newOrderStatus) {
              case 0:
                dispatch.order.setOrdersWaiting([...state.order.ordersWaiting, ...countItemsOnNewOrder])
                break
              case 1:
                dispatch.order.setOrdersBeingPrepared([...state.order.ordersBeingPrepared, ...countItemsOnNewOrder])
                break
              case 2:
                dispatch.order.setOrdersReadyForDelivery([...state.order.ordersReadyForDelivery, ...countItemsOnNewOrder])
                break
              case 3:
                dispatch.order.setOrdersPickedUp([...state.order.ordersPickedUp, ...countItemsOnNewOrder])
                break
              default:
                break
            }
          }
        }
      } catch (error) {
        logError(error as Error)
      }
    },
    async fetchOrderHistory (payload: { storeId: string, dateFrom: string, dateTo: string }) {
      const { storeId, dateFrom, dateTo } = payload

      dispatch.order.startFetching()

      try {
        const storeOrderHistory = await fetchFrom<OrderHistory[]>(`/api/stores/${storeId}/orders/delivered`, {
          method: 'POST',
          body: JSON.stringify({
            startDate: new Date(dateFrom).toISOString(),
            endDate: new Date(dateTo).toISOString(),
          }),
        })
        if (storeOrderHistory) {
          const sortedHistory = storeOrderHistory.sort((a, b) => {
            return new Date(b.date).getTime() - new Date(a.date).getTime()
          })
          dispatch.order.setOrderHistory(sortedHistory)
        } else {
          throw new Error()
        }
      } catch (error) {
        logError(error as Error)
        dispatch.order.setOrderHistory([])
      }
    },
    async fetchCateringHistory (payload) {
      const { storeId, dateFrom, dateTo } = payload
      dispatch.order.startFetching()
      try {
        const data = await fetchFrom<Order[]>(`/api/stores/${storeId}/orders/scheduled/history/${dateFrom}/${dateTo}`)
        if (data) {
          const grouped = data.reduce((prev, current) => {
            const currentDate = format(new Date(current.deliveryDate), 'yyyy-MM-dd')
            if (prev[currentDate]) {
              return {
                ...prev,
                [currentDate]: {
                  date: currentDate,
                  orders: [...prev[currentDate].orders, current],
                },
              }
            } else {
              return {
                ...prev,
                [currentDate]: {
                  date: currentDate,
                  orders: [current],
                },
              }
            }
          }, {} as { [key: string]: { date: string, orders: Order[] } })

          const sortedHistory = Object.keys(grouped).map((i) => ({
            ...grouped[i],
            orders: grouped[i]?.orders.map((cateringOrder) => {
              const itemsGroups = cateringOrder.orderItemsCatering.reduce((prev, current) => {
                // Create a unique key based on variantId + message or name + message
                const key =
                  current.variantId !== emptyGuid && current.variantId + current.message in prev
                    ? current.variantId + current.message
                    : (current.variantId === emptyGuid || current.variantId === null) && current.name + current.message in prev
                      ? current.name + current.message
                      : current.variantId !== emptyGuid
                        ? current.variantId + current.message
                        : current.name + current.message
                
                // Accumulate quantities and priceGross
                const newAmount = prev[key] ? prev[key].amount + current.quantity : current.quantity
                const newPriceGross = prev[key]
                  ? prev[key].priceGross + (current.quantity > 0 ? current.priceGross : 0)
                  : current.quantity > 0
                    ? current.priceGross
                    : 0
                const newVatSubTotal = prev[key]
                  ? prev[key].vatSubTotal + (current.quantity > 0 ? current.vatSubTotal : 0)
                  : current.quantity > 0
                    ? current.vatSubTotal
                    : 0
                
                return {
                  ...prev,
                  [key]: {
                    ...current,
                    amount: newAmount,
                    priceGross: newPriceGross,
                    vatSubTotal: newVatSubTotal,
                    ids: [...(prev[key]?.ids || []), { id: current.id, quantity: current.quantity }],
                  },
                }
              }, {} as { [key: string]: OrderItem })
              
              // Final pass to adjust priceGross with total quantity
              const groupedItems = Object.keys(itemsGroups).map((key) => {
                const item = itemsGroups[key]
                const finalPriceGross = item.amount > 0 ? item.priceGross / item.amount : 0
                const finalVatSubTotal = item.amount > 0 ? item.vatSubTotal / item.amount : 0
                return {
                  ...item,
                  priceGross: finalPriceGross,
                  vatSubTotal: finalVatSubTotal
                }
              })
              
              return {
                ...cateringOrder,
                orderItemsCatering: groupedItems,
              }
            })
              .sort((a, b) => a.deliveryTimeSlot.localeCompare(b.deliveryTimeSlot)),
          }))
            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())

          const sortedOrdersWithHistory = sortedHistory.map((date) => {
            return {
              ...date,
              orders: date.orders.map(order => {
                return getOrderHistory(order) as Order
              })
            }
          })
          dispatch.order.setCateringHistory(sortedOrdersWithHistory)
        } else {
          throw new Error("No data from fetchCateringHistory")
        }
      } catch (error) {
        logError(error as Error)
        dispatch.order.setCateringHistory([])
      }
    },
    async fetchCateringReport (payload) {
      const { storeId, dateFrom, dateTo } = payload
      dispatch.order.startFetching()
      try {
        const data = await fetchFrom<Order[]>(`/api/stores/${storeId}/orders/scheduled/${dateFrom}/${dateTo}`, {
          method: 'GET',
        })
        if (data) {
          const grouped = data.reduce((prev, current) => {
            const currentDate = format(new Date(current.deliveryDate), 'yyyy-MM-dd')
            if (prev[currentDate]) {
              return {
                ...prev,
                [currentDate]: {
                  date: currentDate,
                  orders: [...prev[currentDate].orders, current],
                },
              }
            } else {
              return {
                ...prev,
                [currentDate]: {
                  date: currentDate,
                  orders: [current],
                },
              }
            }
          }, {} as { [key: string]: { date: string, orders: Order[] } })

          const sortedReport = Object.keys(grouped).map((i) => ({
            ...grouped[i],
            orders: grouped[i]?.orders.map((cateringOrder) => {
              const itemsGroups = cateringOrder.orderItemsCatering.reduce((prev, current) => {
                // Create a unique key based on variantId + message or name + message
                const key =
                  current.variantId !== emptyGuid && current.variantId + current.message in prev
                    ? current.variantId + current.message
                    : (current.variantId === emptyGuid || current.variantId === null) && current.name + current.message in prev
                      ? current.name + current.message
                      : current.variantId !== emptyGuid
                        ? current.variantId + current.message
                        : current.name + current.message
                
                // Accumulate quantities and priceGross
                const newAmount = prev[key] ? prev[key].amount + current.quantity : current.quantity
                const newPriceGross = prev[key]
                  ? prev[key].priceGross + (current.quantity > 0 ? current.priceGross : 0)
                  : current.quantity > 0
                    ? current.priceGross
                    : 0
                const newVatSubTotal = prev[key]
                  ? prev[key].vatSubTotal + (current.quantity > 0 ? current.vatSubTotal : 0)
                  : current.quantity > 0
                    ? current.vatSubTotal
                    : 0
                
                return {
                  ...prev,
                  [key]: {
                    ...current,
                    amount: newAmount,
                    priceGross: newPriceGross,
                    vatSubTotal: newVatSubTotal,
                    ids: [...(prev[key]?.ids || []), { id: current.id, quantity: current.quantity }],
                  },
                }
              }, {} as { [key: string]: OrderItem })
              
              // Final pass to adjust priceGross with total quantity
              const groupedItems = Object.keys(itemsGroups).map((key) => {
                const item = itemsGroups[key]
                const finalPriceGross = item.amount > 0 ? item.priceGross / item.amount : 0
                const finalVatSubTotal = item.amount > 0 ? item.vatSubTotal / item.amount : 0
                return {
                  ...item,
                  priceGross: finalPriceGross,
                  vatSubTotal: finalVatSubTotal,
                }
              })
              
              return {
                ...cateringOrder,
                orderItemsCatering: groupedItems,
              }
            })
              .sort((a, b) => a.deliveryTimeSlot.localeCompare(b.deliveryTimeSlot)),
          }))
            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())

          const sortedReportWithHistory = sortedReport.map((date) => {
            return {
              ...date,
              orders: date.orders.map(order => {
                return getOrderHistory(order) as Order
              })
            }
          })
          dispatch.order.setCateringReport(sortedReportWithHistory)
        } else {
          throw new Error("No data from fetchCateringReport")
        }
      } catch (error) {
        logError(error as Error)
        dispatch.order.setCateringReport([])
      }
    },
    async fetchCateringOrders (payload) {
      const { storeId } = payload
      dispatch.order.startFetching()

      try {
        const cateringOrders = await fetchFrom<Order[]>(`/api/stores/${storeId}/orders/scheduled`, {
          method: 'GET',
        })
        if (cateringOrders) {
          // TODO: productType needed on every item in orderItemsCatering to distinguish between an ordinary product and a buffet product
          const cateringOrdersNoCancelledOrders = cateringOrders.filter((order) => order.status === 0)

          const grouped = cateringOrdersNoCancelledOrders.reduce((prev, current) => {
            const currentDate = format(new Date(current.deliveryDate), 'yyyy-MM-dd')
            if (prev[currentDate]) {
              return {
                ...prev,
                [currentDate]: {
                  date: currentDate,
                  orders: [...prev[currentDate].orders, current],
                },
              }
            } else {
              return {
                ...prev,
                [currentDate]: {
                  date: currentDate,
                  orders: [current],
                },
              }
            }
          }, {} as { [key: string]: { date: string, orders: Order[] } })

          const sortedOrders = Object.keys(grouped).map((i) => ({
            ...grouped[i],
            orders: grouped[i]?.orders.map((cateringOrder) => {
              const itemsGroups = cateringOrder.orderItemsCatering.reduce((prev, current) => {
                // Create a unique key based on variantId + message or name + message
                const key =
                  current.variantId !== emptyGuid && current.variantId + current.message in prev
                    ? current.variantId + current.message
                    : (current.variantId === emptyGuid || current.variantId === null) && current.name + current.message in prev
                      ? current.name + current.message
                      : current.variantId !== emptyGuid
                        ? current.variantId + current.message
                        : current.name + current.message
                
                // Accumulate quantities and priceGross
                const newAmount = prev[key] ? prev[key].amount + current.quantity : current.quantity
                const newPriceGross = prev[key]
                  ? prev[key].priceGross + (current.quantity > 0 ? current.priceGross : 0)
                  : current.quantity > 0
                    ? current.priceGross
                    : 0
                const newVatSubTotal = prev[key]
                  ? prev[key].vatSubTotal + (current.quantity > 0 ? current.vatSubTotal : 0)
                  : current.quantity > 0
                    ? current.vatSubTotal
                    : 0
                
                return {
                  ...prev,
                  [key]: {
                    ...current,
                    amount: newAmount,
                    priceGross: newPriceGross,
                    vatSubTotal: newVatSubTotal,
                    ids: [...(prev[key]?.ids || []), { id: current.id, quantity: current.quantity }],
                  },
                }
              }, {} as { [key: string]: OrderItem })
              
              // Final pass to adjust priceGross with total quantity
              const groupedItems = Object.keys(itemsGroups).map((key) => {
                const item = itemsGroups[key]
                const finalPriceGross = item.amount > 0 ? item.priceGross / item.amount : 0
                const finalVatSubTotal = item.amount > 0 ? item.vatSubTotal / item.amount : 0
                return {
                  ...item,
                  priceGross: finalPriceGross,
                  vatSubTotal: finalVatSubTotal,
                }
              })
              
              return {
                ...cateringOrder,
                orderItemsCatering: groupedItems,
              }
            })
              .sort((a, b) => a.deliveryTimeSlot.localeCompare(b.deliveryTimeSlot)),
          }))
            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())

          const sortedOrdersWithHistory = sortedOrders.map((date) => {
            return {
              ...date,
              orders: date.orders.map(order => {
                return getOrderHistory(order) as Order
              })
            }
          })
          dispatch.order.setCateringOrders(sortedOrdersWithHistory)
        } else {
          throw new Error("No data from fetchCateringOrders")
        }
      } catch (error) {
        logError(error as Error)
        dispatch.order.setCateringOrders([])
      }
    },
    async cancelCateringOrders (payload: { storeId: string, orderId: string }) {
      const { storeId, orderId } = payload
      try {
        const cancelled = await fetchFrom(`/api/orders/${orderId}/${storeId}/cancel`, {
          method: 'GET',
        })
        dispatch.order.fetchCateringOrders({ storeId })
        console.log(cancelled)
      } catch (error) {
        logError(error as Error)
      }
    },
    async printCateringOrder(payload, state) {
      const { storeId, orderIds, userId, type, isSingleOrder = true } = payload 
      try {
        const data = isSingleOrder ? { orderIds: [orderIds], storeId, userId, type } : { orderIds, storeId, userId, type }
        const res = await sendServiceData('orderApi', 'v1', '/order/setordersasprintedbytype', data, 'PUT')
     
        if (res) {
          const cateringOrders = state.order.cateringOrders || []
          const orders = cateringOrders.map((order) => {
            return {
              ...order,
              orders: order.orders.map((o) => {
                if (isSingleOrder && o.id === res.data[0].id) {
                  return {
                    ...o,
                    printedByUsersWithType: [...res.data[0].printedByUsersWithType]
                  }
                } else if (!isSingleOrder && orderIds.indexOf(o.id) > -1) {
                  const updatedOrderData = res.data.find((order: Order) => order.id === o.id)
                  if (updatedOrderData) {
                    return {
                      ...o,
                      printedByUsersWithType: [...updatedOrderData.printedByUsersWithType]
                    }
                  }
                }
                return o
              }),
            }
          })
          dispatch.order.setCateringOrders(orders)
        }
      } catch (error) {
        logError(error as Error)
      }
    },     
    async updateStatusAsync (payload, state) {
      const { storeId, orderId, status } = payload || {}

      try {
        const res = await fetchFrom<OrderStatusUpdate>(`/api/stores/${storeId}/orders/${orderId}/${status}/V2`, {
          method: 'PUT',
        })

        if (res) {
          const storeOrders = state.order.allOrders || []
          const orders = storeOrders.map((order) => {
            if ((order as Order).id === res.id) { //id is undefined if anonymous thus false
              return {
                ...order,
                status: res.status,
              }
            }
            return order
          })
          dispatch.order.setAllOrders(orders)

          const { waiting = [], beingPrepared = [], readyForDelivery = [], pickedUp = [] } = groupByStatus<Order>(orders)
          dispatch.order.setOrdersWaiting(waiting)
          dispatch.order.setOrdersBeingPrepared(beingPrepared)
          dispatch.order.setOrdersReadyForDelivery(readyForDelivery)
          dispatch.order.setOrdersPickedUp(pickedUp)

        }
      } catch (error) {
        logError(error as Error)
      }
    },
    updateStatusState (payload, state) {
      const storeOrders = state.order.allOrders || []
      const { existingOrder, orderStatus } = payload

      const orders = storeOrders.map((order) => {
        if ((order as Order).id === existingOrder.id) { //id is undefined if anonymous thus false
          return {
            ...order,
            status: orderStatus,
          }
        }
        return order
      })
      dispatch.order.setAllOrders(orders)
      const { waiting = [], beingPrepared = [], readyForDelivery = [], pickedUp = [] } = groupByStatus<Order>(orders)
      dispatch.order.setOrdersWaiting(waiting)
      dispatch.order.setOrdersBeingPrepared(beingPrepared)
      dispatch.order.setOrdersReadyForDelivery(readyForDelivery)
      dispatch.order.setOrdersPickedUp(pickedUp)
    },
    async updateItemStatusAsync (payload, state) {
      const { storeId, orderId, itemId, itemStatus } = payload
      const storeOrders = state.order.allOrders || []

      try {
        const res = await fetchFrom<OrderStatusUpdate>(`/api/stores/${storeId}/orders/${orderId}/orderitems/${itemId}/${itemStatus}/V2`, {
          method: 'PUT',
        })


        const orders = storeOrders.map((order) => {
          if ((order as Order).id === res.id) { //id is undefined if anonymous thus false
            return {
              ...order,
              orderItemsGrocery: (order as Order).orderItemsGrocery.map((groceryItem) => {
                if (groceryItem.id === res.itemId && groceryItem.status !== res.status) {
                  return {
                    ...groceryItem,
                    status: res.status,
                  }
                }
                return groceryItem
              }),
            }
          }
          return order
        })
        dispatch.order.setAllOrders(orders)
        const { waiting = [], beingPrepared = [], readyForDelivery = [], pickedUp = [] } = groupByStatus<Order>(orders)
        dispatch.order.setOrdersWaiting(waiting)
        dispatch.order.setOrdersBeingPrepared(beingPrepared)
        dispatch.order.setOrdersReadyForDelivery(readyForDelivery)
        dispatch.order.setOrdersPickedUp(pickedUp)
      } catch (error) {
        logError(error as Error)
      }
    },
    updateItemStatus (payload, state) {
      const { orderId, itemId, itemStatus } = payload
      const storeOrders = state.order.allOrders || []

      const orders = storeOrders.map((order) => {
        if ((order as Order).id === orderId) { //id is undefined if anonymous thus false
          return {
            ...order,
            orderItemsGrocery: (order as Order).orderItemsGrocery.map((groceryItem) => {
              if (groceryItem.id === itemId && groceryItem.status !== itemStatus) {
                return {
                  ...groceryItem,
                  status: itemStatus,
                }
              }
              return groceryItem
            }),
          }
        }
        return order
      })
      dispatch.order.setAllOrders(orders)
      const { waiting = [], beingPrepared = [], readyForDelivery = [], pickedUp = [] } = groupByStatus<Order>(orders)
      dispatch.order.setOrdersWaiting(waiting)
      dispatch.order.setOrdersBeingPrepared(beingPrepared)
      dispatch.order.setOrdersReadyForDelivery(readyForDelivery)
      dispatch.order.setOrdersPickedUp(pickedUp)
    },
    async editCateringOrder(payload: { orderId: string, storeId: string, userName: string, operations: OrderUpdateOperation[] }, state) {
      const { orderId, storeId, userName, operations } = payload
    
      try {
        const res = await fetchFrom<UpdatedOrder>(`/api/stores/${storeId}/orders/update`, {
          method: 'POST',
          body: JSON.stringify({
            userName: userName,
            orderId: orderId,
            storeId: storeId,
            operations: operations
          }),
        })
    
        if (res.updatedOrder) {
          const updatedOrderDate = format(new Date(res.updatedOrder.deliveryDate), 'yyyy-MM-dd')
          const orderItemsCatering = groupCateringOrderItems(res.updatedOrder.orderItemsCatering)
          const updatedOrder = {
            ...res.updatedOrder,
            orderItemsCatering: orderItemsCatering,
            deliveryDate: updatedOrderDate,
            deliveryTimeSlot: res.updatedOrder.deliveryTimeSlot,
            orderHistory: res.updatedOrder.orderHistory,
            storeComment: res.updatedOrder.storeComment,
            totalPrice: res.updatedOrder.totalPrice,
            paymentInternalDisplayName: res.updatedOrder.paymentInternalDisplayName,
            paymentProviderType: res.updatedOrder.paymentProviderType
          }
    
          let dateExists = false
    
          // Remove the order from the old date
          state.order.cateringOrders = state.order.cateringOrders.map(cateringOrder => {
            cateringOrder.orders = cateringOrder.orders.filter(order => order.id !== orderId)
            return cateringOrder
          }).filter(cateringOrder => cateringOrder.orders.length > 0) // Remove empty dates
    
          // Add the order to the new date
          state.order.cateringOrders = state.order.cateringOrders.map(cateringOrder => {
            if (cateringOrder.date === updatedOrderDate) {
              dateExists = true
              cateringOrder.orders.push(getOrderHistory(updatedOrder) as Order)
              cateringOrder.orders.sort((a, b) => a.deliveryTimeSlot.localeCompare(b.deliveryTimeSlot))
            }
            return cateringOrder
          })
    
          if (!dateExists) {
            state.order.cateringOrders.push({
              date: updatedOrderDate,
              orders: [getOrderHistory(updatedOrder) as Order]
            })
          }
    
          state.order.cateringOrders.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    
          dispatch.order.setCateringOrders([...state.order.cateringOrders])
          return true
        }
      } catch (error) {
        logError(error as Error)
        return false
      }
    },
    async getPaymentMethods (storeId: string):  Promise<PaymentMethod[] | undefined>  {
      const headers = {
        'x-client-name': 'icacatering'
      }
      try {
        const res = await getServiceData(
          'checkoutApi',
          'v1',
          `/paymentmethods/${storeId}`,
          headers
        )
        if (res && res.data) {
          return res.data as PaymentMethod[]
        }
      } catch (error) {
        logError(error as Error)
      }
    },
    async getDeliveryProviders(storeId: string):  Promise<DeliveryProvider[] | undefined>  {
      const headers = {
        'x-client-name': 'icacatering'
      }
      try {
        const res = await getServiceData(
          'foodApi',
          'v1',
          `/stores/${storeId}/deliveryProviders/active`,
          headers
        )
        if (res && res.data) {
          return res.data as DeliveryProvider[]
        }
      } catch (error) {
        logError(error as Error)
      }
    },
    async getCateringProduct (productId: string, state) {
      try {
        const res = await getServiceData('foodApi', 'v1', `/catering/${state.store.selectedStore?.id}/products/${productId}`)
        if (res && res.data) {
          return res.data
        }
      } catch (error) {
        logError(error as Error)
      }
    },
    async getCateringProducts (storeId: string) {
      try {
        const res = await getServiceData('foodApi', 'v1', `/catering/${storeId}/products`)

        if (res && res.data) {
          const activeProducts: Product[] = res.data.filter((product: Product) => product.active === true)
          
          const activeCategories = activeProducts.reduce((acc, product) => {
            product.categories.forEach(category => {
              if (category.active) {
                const existingCategory = acc.find(cat => cat.categoryName === category.name)
                if (existingCategory) {
                  existingCategory.products.push(product)
                } else {
                  acc.push({ categoryName: category.name, products: [product] })
                }
              }
            })
            return acc
          }, [] as { categoryName: string, products: Product[] }[])

          return activeCategories
        }
      } catch (error) {
        logError(error as Error)
      }
    },
    async createOrder (payload: CreateOrderPayload): Promise<Order | undefined>  {
      try {
        const res = await sendServiceData('orderApi', 'v1', '/order', payload, 'POST')

        if (!res || !res.ok) {
          throw new Error(String('Failed to create order'))
        }

        return res.data as Order
      } catch (error) {
        logError(error as Error)
        throw error
      }
    }
  }),
})
